import { submitConnectEnquiry } from "@/lib/utils/connect-enquiry-api";
import { restrictToTenDigits } from "@/lib/utils/connect-form-validation";
import { useToastContext } from "@/providers/toastProvider";
import { useDpStore } from "@/store/dpStore";
import { useQuestionsStore } from "@/store/questionsStore";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import Person2OutlinedIcon from "@mui/icons-material/Person2Outlined";
import { Button, Dialog, Snackbar } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import Image from "next/image";
import { useEffect, useMemo, useRef } from "react";
import EnquiryTextField from "./EnquiryTextField";
import { pushGtmEvent } from "@/lib/utils/gtmEventHandler";
import { isProfileConnected } from "@/lib/utils/connected-profiles-storage";
export default function EnquiryModal({
  digitalProfile,
  setSuccessModalOpen
}) {
  const dpStore = useDpStore();
  const {
    enquiryFormData,
    setEnquiryFormData,
    isEnquiryModalOpen,
    setIsEnquiryModalOpen,
    setIsMapModalOpen,
    setShowTempButton
  } = dpStore;
  const {
    showSnackbar
  } = useToastContext();
  const isFormValid = useMemo(() => {
    return enquiryFormData.whatsappNumber.length === 10 && enquiryFormData.fullName.length >= 2 && enquiryFormData.siteLocation.length >= 2 && !enquiryFormData.whatsappNumberError && !enquiryFormData.fullNameError && !enquiryFormData.siteLocationError;
  }, [enquiryFormData]);
  const {
    storeAnswers
  } = useQuestionsStore();
  const dialogSlotProps = useMemo(() => ({
    paper: {
      sx: {
        width: "364px",
        maxWidth: "364px",
        borderRadius: "28px",
        overflow: "auto",
        position: "relative",
        background: "white",
        padding: 0,
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        margin: 0,
        // Hide scrollbar for Chrome, Safari and Opera
        "&::-webkit-scrollbar": {
          display: "none"
        },
        // Hide scrollbar for IE, Edge and Firefox
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "@media (max-width: 600px)": {
          width: "calc(100% - 2rem)",
          maxWidth: "calc(100% - 2rem)",
          margin: "1.875rem 1rem"
        }
      }
    },
    backdrop: {
      sx: {
        backgroundColor: "rgba(0, 0, 0, 0.5)"
      }
    }
  }), []);
  const eventPayload = {
    homeowner_contact_number: enquiryFormData.whatsappNumber,
    homeowner_site_address: enquiryFormData.siteLocation,
    pincode: typeof window !== "undefined" ? localStorage.getItem("multiplyonboarding_pincode") : "",
    contractor_name: digitalProfile?.profileName,
    contractor_id: digitalProfile?.contractorId,
    contractor_phone_number: digitalProfile?.phoneNumber,
    contractor_profile_id: digitalProfile?.profileName
  };
  const getQaResponse = () => {
    // Find the starting question (one with previous === null)
    const findStartQuestionId = answers => {
      return Object.keys(answers).find(id => answers[id]?.navigation?.previous === null);
    };

    // Get the next question ID based on navigation and answer
    const getNextQuestionId = question => {
      const nav = question?.navigation;
      if (!nav) return null;

      // Conditional navigation
      if (nav.type === "conditional" && Array.isArray(nav.conditions)) {
        const answerValue = question.value;
        const matchingCondition = nav.conditions.find(condition => condition.when === answerValue);
        return matchingCondition?.next ?? null;
      }

      // Linear navigation
      return nav.next ?? null;
    };

    // Traverse the flow starting from the first question
    const onBoardAnswers = {};
    const startQuestionId = findStartQuestionId(storeAnswers);
    if (!startQuestionId) {
      // Fallback: if no start question found, return empty object
      return JSON.stringify(onBoardAnswers);
    }
    let currentQuestionId = startQuestionId;
    const visited = new Set();

    // Follow the navigation flow
    while (currentQuestionId && currentQuestionId !== "end" && !visited.has(currentQuestionId)) {
      visited.add(currentQuestionId);
      const currentQuestion = storeAnswers[currentQuestionId];
      if (currentQuestion && currentQuestion.question && currentQuestion.answer) {
        onBoardAnswers[currentQuestion.question_key] = currentQuestion.answer;
      }

      // Get the next question based on navigation
      currentQuestionId = getNextQuestionId(currentQuestion);
    }
    return JSON.stringify(onBoardAnswers);
  };
  const handleClose = () => {
    setIsEnquiryModalOpen(false);
    const isEnquiryAlreadySent = isProfileConnected(digitalProfile?.contractorId);
    if (isEnquiryAlreadySent) {
      pushGtmEvent({
        event_name: "enquire_again_form_action_taken",
        event_action: "prefilled_form_cancelled",
        ...eventPayload
      });
    } else {
      pushGtmEvent({
        event_name: "enquiry_form_action_taken",
        event_action: "cancel_clicked",
        ...eventPayload
      });
    }
  };
  const enquiryMutation = useMutation({
    mutationFn: async data => {
      return await submitConnectEnquiry(data);
    },
    onSuccess: () => {
      setShowTempButton(true);
      setSuccessModalOpen(true);
      setIsEnquiryModalOpen(false);
    },
    onError: error => {
      let errorMessage = "Failed to submit enquiry. Please try again.";
      if (error.message) {
        errorMessage = error.message;
      } else if (error.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (error.status === 400) {
        errorMessage = "Invalid data. Please check your inputs.";
      } else if (error.status === 401 || error.status === 403) {
        errorMessage = "Unauthorized. Please refresh and try again.";
      } else if (error.status === 429) {
        errorMessage = "Too many requests. Please try again later.";
      }
      showSnackbar(errorMessage, "error");
    }
  });
  const handleSubmit = () => {
    let whatsappNumberError = false;
    let fullNameError = false;
    let siteLocationError = false;
    if (enquiryFormData.whatsappNumber.length !== 10) {
      whatsappNumberError = true;
    }
    if (enquiryFormData.fullName.length < 2) {
      fullNameError = true;
    }
    if (enquiryFormData.siteLocation.length < 2) {
      siteLocationError = true;
    }
    setEnquiryFormData({
      ...enquiryFormData,
      whatsappNumberError,
      fullNameError,
      siteLocationError
    });
    if (whatsappNumberError || fullNameError || siteLocationError) {
      return;
    }
    const payload = {
      homeownerNumber: enquiryFormData.whatsappNumber,
      homeownerName: enquiryFormData.fullName,
      siteAddress: enquiryFormData.siteLocation,
      qaResponse: getQaResponse(),
      pincode: localStorage.getItem("multiplyonboarding_pincode"),
      contractorName: digitalProfile?.profileName,
      contractorUserId: digitalProfile?.contractorId,
      contractorNumber: digitalProfile?.phoneNumber
    };
    enquiryMutation.mutate(payload);
    const isEnquiryAlreadySent = isProfileConnected(digitalProfile?.contractorId);
    if (!isEnquiryAlreadySent) {
      pushGtmEvent({
        event_name: "enquiry_submitted",
        homeowner_name: enquiryFormData.fullName,
        ...eventPayload
      });
    } else {
      pushGtmEvent({
        event_name: "enquire_again_form_action_taken",
        event_action: "prefilled_form_submitted",
        ...eventPayload
      });
    }
  };
  const handleWhatsappChange = e => {
    const inputValue = e.target.value;
    const restrictedValue = restrictToTenDigits(inputValue);
    setEnquiryFormData({
      ...enquiryFormData,
      whatsappNumber: restrictedValue,
      whatsappNumberError: false
    });
  };
  const handleFullNameChange = e => {
    setEnquiryFormData({
      ...enquiryFormData,
      fullName: e.target.value,
      fullNameError: false
    });
  };
  const handleSiteLocationClick = () => {
    pushGtmEvent({
      event_name: "site_location_field_clicked",
      ...eventPayload
    });
    setIsMapModalOpen(true);
  };
  const eventSent = useRef(false);
  useEffect(() => {
    if (isEnquiryModalOpen && !eventSent.current) {
      eventSent.current = true;
      pushGtmEvent({
        event_name: "enquiry_form_viewed",
        ...eventPayload
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnquiryModalOpen]);
  const fromCarpenterLandingPage = typeof window !== "undefined" && sessionStorage.getItem("landingPage") === "carpenter";
  const primaryLocation = enquiryFormData.siteLocation.split(",")[0];
  const contractorHelperText = "Site location is shared only with the contractor to ensure easy navigation on site visit";
  const carpenterHelperText = "Site location is shared only with the carpenter to ensure easy navigation on site visit";
  return <Dialog open={isEnquiryModalOpen} onClose={handleClose} scroll="paper" slotProps={dialogSlotProps} key="connect-modal">
      <div className="relative w-full h-[53px] flex-shrink-0">
        <Image src="/images/multiply-background-image.avif" alt="background" fill className="object-cover" priority />
      </div>
      <div className="p-5 flex flex-col gap-6 m3-headline-small">
        <p>Connect with {digitalProfile?.profileName || "Contractor"}</p>

        <div className="flex flex-col items-center gap-6 ">
          <EnquiryTextField
        // helperText={fromCarpenterLandingPage ? "We’ll share the carpenter’s contact details with you on WhatsApp." : "We’ll share the contractor’s contact details with you on WhatsApp."}
        errorMessage="Please enter a valid WhatsApp number" value={enquiryFormData.whatsappNumber} onChange={handleWhatsappChange} hasError={enquiryFormData.whatsappNumberError} label="Enter Your Phone Number" icon={<LocalPhoneOutlinedIcon className="!text-[--colorSecondarySecondaryContainer]  mt-4 " />} type="tel" />
          <EnquiryTextField errorMessage="Please enter your full name" hasError={enquiryFormData.fullNameError} value={enquiryFormData.fullName} onChange={handleFullNameChange} label="Enter your full name" icon={<Person2OutlinedIcon className="!text-[--colorSecondarySecondaryContainer]  mt-4" />} />

          <EnquiryTextField helperText={fromCarpenterLandingPage ? carpenterHelperText : contractorHelperText} valueTitle={primaryLocation} errorMessage="Please enter your site location" hasError={enquiryFormData.siteLocationError} value={enquiryFormData.siteLocation} onClick={handleSiteLocationClick} label="Enter Your Site Location" multiline={4} icon={<LocationOnOutlinedIcon className="!text-[--colorSecondarySecondaryContainer]  mb-6" />} />
        </div>
      </div>

      <div className="flex justify-end items-center p-5 border-t-[1px] ">
        <Button variant="text" className="m3-label-large" onClick={handleClose} sx={{
        color: "var(--textLinkOnSurfaceLink, #004BEE)",
        textTransform: "none",
        padding: "12px 24px",
        "&:hover": {
          backgroundColor: "rgba(0, 75, 238, 0.04)"
        }
      }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant={isFormValid ? "contained" : "outlined"} loading={enquiryMutation.isPending} disabled={!isFormValid} sx={{
        borderRadius: "100px"
      }}>
          Submit Enquiry
        </Button>
      </div>
    </Dialog>;
}